import React, { useState } from 'react';
import { Text, Stack, Group, Button, Alert, Code } from '@mantine/core';
import { Dropzone, FileWithPath } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import RestaurantSelector from '../../../components/upload/RestaurantSelector';
import api from '../../../services/api';
import { RestaurantOption } from '../../../utils/uploadFormData';

/**
 * Admin CSV Import page - imports menu items from exported CSV format
 * No AI processing - direct import of all fields and props
 */
export function AdminImportCSV() {
  const navigate = useNavigate();
  const [csvFile, setCsvFile] = useState<FileWithPath | null>(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState<string | null>(null);
  const [newRestaurantName, setNewRestaurantName] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [description, setDescription] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState<string>('jp');
  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch restaurants on mount
  React.useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        const response = await api.get('/restaurants');
        const list = Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
        const restaurantOptions: RestaurantOption[] = list.map((r: any) => ({
          value: r.id,
          label: r.name
        }));
        setRestaurants(restaurantOptions);
      } catch (error) {
        console.error('Error fetching restaurants:', error);
      }
    };
    fetchRestaurants();
  }, []);

  const handleFileDrop = (droppedFiles: FileWithPath[]) => {
    if (droppedFiles.length > 0) {
      const file = droppedFiles[0];
      if (file.name.toLowerCase().endsWith('.csv')) {
        setCsvFile(file);
      } else {
        notifications.show({
          title: 'Error',
          message: 'Please upload a CSV file',
          color: 'red',
        });
      }
    }
  };

  const removeFile = () => {
    setCsvFile(null);
  };

  const handleImport = async () => {
    // Validation
    if (!csvFile) {
      notifications.show({
        title: 'Error',
        message: 'Please select a CSV file',
        color: 'red',
      });
      return;
    }

    if (!selectedRestaurant) {
      notifications.show({
        title: 'Error',
        message: 'Please select a restaurant',
        color: 'red',
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('csvFile', csvFile);
      
      // Add restaurant data
      if (selectedRestaurant === 'other') {
        formData.append('name', newRestaurantName);
      } else {
        const selectedRestaurantData = restaurants.find(r => r.value === selectedRestaurant);
        if (selectedRestaurantData) {
          formData.append('restaurantId', selectedRestaurant);
        }
      }

      // Add source language
      formData.append('sourceLanguage', sourceLanguage);

      const response = await api.post('/menus/import/csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      notifications.show({
        title: 'Success',
        message: `Imported ${response.data.created} menu items successfully`,
        color: 'green',
      });

      // Navigate to the created menu
      if (response.data.menuId) {
        navigate(`/admin/menus/edit/${response.data.menuId}`);
      }
    } catch (error: any) {
      console.error('Error importing CSV:', error);
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.error || error?.message || 'Failed to import CSV',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack gap="md" p="md">
      <Text size="xl" fw={700}>Import Menu from CSV</Text>

      <Alert title="CSV Import Format" color="blue" mb="md">
        <Text size="sm" mb="xs">
          Import menu items from a CSV exported via the menu edit page. All props fields are fully
          expanded into dot-notation columns — you can edit them directly in a spreadsheet and re-import.
        </Text>
        <Text size="sm" mt="xs">
          • <strong>Base fields</strong>: name, description, price, category, ingredients, images, imageUrl, cloudinaryPublicId (id / createdAt / updatedAt are ignored)<br/>
          • <strong>Props columns</strong>: dot-notation paths, e.g. <code>props.enLocalization.name</code>, <code>props.translations.en.name.text</code>, <code>props.aiDetails.description</code><br/>
          • <strong>Arrays</strong>: stored as JSON strings in the CSV (e.g. <code>["item1","item2"]</code>)<br/>
          • <strong>No AI processing</strong>: items are imported directly without re-running any AI steps
        </Text>
      </Alert>

      <Dropzone
        onDrop={handleFileDrop}
        accept={{
          'text/csv': ['.csv'],
        }}
        mb="md"
        maxFiles={1}
        disabled={loading}
      >
        <Text ta="center" size="lg">
          Drag and drop a CSV file here, or click to select file
        </Text>
        <Text ta="center" size="sm" c="dimmed" mt={7}>
          Only CSV files are accepted
        </Text>
      </Dropzone>

      {csvFile && (
        <Stack mb="md">
          <Text fw={500}>CSV file to import:</Text>
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm" style={{ wordBreak: 'break-all' }}>
              {csvFile.name} ({(csvFile.size / 1024).toFixed(1)} KB)
            </Text>
            <Button
              variant="subtle"
              color="red"
              size="xs"
              onClick={removeFile}
              disabled={loading}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      )}

      {/* Restaurant Selection */}
      <RestaurantSelector
        selectedRestaurant={selectedRestaurant}
        onRestaurantChange={setSelectedRestaurant}
        newRestaurantName={newRestaurantName}
        onNewRestaurantNameChange={setNewRestaurantName}
        adminNotes={adminNotes}
        onAdminNotesChange={setAdminNotes}
        description={description}
        onDescriptionChange={setDescription}
        loading={loading}
        onRestaurantsChange={setRestaurants}
      />

      {/* Source Language Selection */}
      <Stack gap="md" mb="md">
        <Text size="sm" fw={500}>Source Language</Text>
        <Text size="xs" c="dimmed">
          The original language of the menu items. This will be stored as the source language in the menu.
        </Text>
        <Group>
          <select
            value={sourceLanguage}
            onChange={(e) => setSourceLanguage(e.target.value)}
            disabled={loading}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ced4da',
              fontSize: '14px',
              minWidth: '200px'
            }}
          >
            <option value="jp">Japanese</option>
            <option value="ko">Korean</option>
            <option value="zh">Chinese</option>
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
            <option value="de">German</option>
            <option value="it">Italian</option>
            <option value="pt">Portuguese</option>
            <option value="ru">Russian</option>
          </select>
        </Group>
      </Stack>

      {/* Import Button */}
      <Group justify="flex-end">
        <Button
          onClick={handleImport}
          loading={loading}
          disabled={!csvFile || !selectedRestaurant}
          size="lg"
        >
          Import CSV
        </Button>
      </Group>
    </Stack>
  );
}
